import { useEffect, useRef, useState, type DragEvent } from "react";

import { Box, Skeleton } from "@mui/material";
// GCS, NOT Amplify. `aws-amplify`'s Storage authenticates with COGNITO
// credentials; g2 is a GCIP tenant, so there are none and every call threw
// "Error loading documents: Error: No credentials". A crawl of all 78 nav
// destinations found that on the Files/Documents tab of ELEVEN modules —
// Customer Quotes, Follow Ups, Sales Orders, Defect Reporting, Tasks, Work
// Orders, Supplier Quotes, Purchase Orders, Service Orders, Inventory/Tool
// Requests and Documents. One import, eleven broken tabs.
//
// src/storage/gcs.ts already existed for exactly this and says so in its own
// header: "Drop-in replacement for Amplify Storage… replace: import { Storage }
// from 'aws-amplify' with: import { GCSStorage as Storage }". It wraps the five
// /v6/storage/* routes over gs://aeronet-vault-dev. src/blank/tabs/BlankDocuments.tsx
// documents it as the canonical pattern. Vault simply never adopted it — a Rule 0
// miss whose blast radius was eleven modules.
//
// Safe on every tenant this tree serves: GCS_BUCKET_NAME=aeronet-vault-dev is set
// on the Cognito products too (verified live on aeronet-fast-api, -crm and -g2),
// and CLAUDE.md records the vault as the shared SCM+CRM Files-tab store. The
// .nz prod installs run a different frontend tree and are untouched by this.
import { GCSStorage as Storage } from "../storage/gcs";
import { UploaderEvents, Vault as VaultBase } from "dhx-vault-package";
import "dhx-vault-package/codebase/vault.css";
import { useSnackbar } from "notistack";


import { AttachmentService } from "../attachments/AttachmentService";
import { attachmentForUpload } from "../attachments/storedFile";
import { DocumentAccess } from "../documents/types";

export function downloadBlob(blob, filename) {
  // from https://docs.amplify.aws/lib/storage/download/q/platform/js/#file-download-option
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  const clickHandler = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.removeEventListener("click", clickHandler);
    }, 150);
  };
  a.addEventListener("click", clickHandler, false);
  a.click();
  return a;
}

/** Why the server refused a file action, when it said (a 403 is a permission). */
export function uploadRefusal(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 403 ? ": you don't have permission to change this record's files" : "";
}

function Vault(props: any) {
  const vaultRef = useRef<HTMLDivElement | null>(null);
  const vaultInstanceRef = useRef<any>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!vaultRef.current) return;

    if (props.access) {
      switch (props.access) {
        case DocumentAccess.Public:
          Storage.configure({
            customPrefix: { public: "storage/external/" },
          });
          break;
        case DocumentAccess.Protected:
          Storage.configure({ level: "protected" });
          break;
        case DocumentAccess.Private:
          Storage.configure({ level: "private" });
          break;
      }
    }

    // @ts-ignore
    const vault = new VaultBase(vaultRef.current, {
      data: props.data,
      mode: props.mode,
      uploader: {
        autosend: props.autosend,
        target: props.target,
      },
      toolbar: props.toolbar,
    });
    vaultInstanceRef.current = vault;

    vault.events.on(UploaderEvents.beforeUploadFile, function (file) {
      if (props.readOnly) {
        enqueueSnackbar("View only. You can't upload files.", {
          variant: "error",
        });
        return false;
      }
      console.log(file.id);

      let key = `${props.path}${file.name!.replace(/[^\w\d_\-.]+/gi, "")}`;

      const storageConfig = {
        contentDisposition: `attachment; filename="${file.name}"`,
        metadata: { name: file.name! },
      };

      Storage.put(key, file.file, storageConfig)
        .then((resp) => {
          console.log(resp);
          vault.events.fire(UploaderEvents.uploadFile, [file, resp]);
        })
        .catch((e) => {
          // The server refuses an upload the caller may not make on this record
          // (the module's edit permission, a pilot's own records only — H282).
          // Without this the row sat "in progress" forever with no word why.
          console.error(e);
          enqueueSnackbar(`${file.name} was not uploaded${uploadRefusal(e)}`, {
            variant: "error",
          });
          vault.data.update(file.id!, { status: "failed" });
        });

      vault.data.update(file.id!, { status: "inprogress" });
      return false;
    });

    vault.events.on(UploaderEvents.uploadFile, async function (file, extra) {
      console.log("The file is uploaded");
      // extra.key is where the server STORED it — a free image-2.jpg when image.jpg
      // was taken (H282) — so the row keeps that as filename and the picked name as
      // title. Delete below finds the row by the stored key.
      const data = attachmentForUpload({
        displayName: file.name!,
        storedKey: extra!.key,
        source: props.source,
        id: props.id,
        access: props.access,
      });

      try {
        const attachment = await AttachmentService.create(data);
        vault.data.update(file.id!, {
          key: extra!.key,
          status: "uploaded",
          attachmentId: attachment.id,
        });
      } catch (e) {
        console.error(e);
        enqueueSnackbar("File uploaded but its attachment record failed", {
          variant: "error",
        });
        vault.data.update(file.id!, { key: extra?.key, status: "uploaded" });
      }
    });

    vault.events.on("beforeRemove", async function (file) {
      if (props.readOnly) {
        enqueueSnackbar("View only. You can't delete files.", {
          variant: "error",
        });
        return false;
      }
      try {
        await Storage.remove(file.key!);
      } catch (e) {
        console.error(e);
        enqueueSnackbar(`${file.name} was not deleted${uploadRefusal(e)}`, {
          variant: "error",
        });
        return false;
      }
      return true;
    });

    vault.events.on("afterRemove", async function (file) {
      if (file.attachmentId) {
        AttachmentService.delete(file.attachmentId);
      } else if (props.source && file.key) {
        // A listed file's row is found by its STORED key, server-side (H282). The old
        // lookup matched `filename == <listed name>`, but rows kept the picked name
        // ("My receipt.pdf") while the key kept the sanitised one ("Myreceipt.pdf"),
        // so every such delete left an orphan row.
        AttachmentService.deleteForKey(props.source, props.id, file.key);
      }
    });

    vault.events.on("removeAll", async function () {
      if (props.readOnly) {
        enqueueSnackbar("View only. You can't delete files.", {
          variant: "error",
        });
        return false;
      }
      const objs = await Storage.list(props.path);
      await Promise.allSettled(objs.map((obj) => Storage.remove(obj.key!)));

      await AttachmentService.deleteAll(props.source, props.id);
    });

    Storage.list(props.path)
      .then((results) => {
        console.debug(results);
        // filters out sub folders
        const docs = results.filter((result) => {
          // ignore the folder object
          if (result.size === 0) {
            return false;
          }

          // compare the object prefix to the path prop
          if (result.key) {
            const keyPrefix =
              result.key.split("/").slice(0, -1).join("/") + "/";
            if (keyPrefix === props.path) {
              return true;
            }
          }

          // anything that doesn't exactly match the path will be filtered out
          return false;
        });

        console.debug(docs);

        const dataset = docs.map((obj) => {
          return {
            id: obj.key,
            name: new URL(obj.key!, "https://example.com").pathname
              .split("/")
              .pop(),
            size: obj.size,
            key: obj.key,
          };
        });

        console.debug(dataset);

        setLoading(false);
        // @ts-ignore
        vault.data.parse(dataset);
      })
      .catch((error) => {
        console.error("Error loading documents: ", error);
        setLoading(false);
        // NO HARDCODED FALLBACK DATA HERE. This catch() used to inject four
        // invented aviation documents --- including
        // "Certificate_of_Airworthiness_2026.pdf" and an engine logbook entry
        // --- into the file list whenever the real document load FAILED, with
        // no product gate. Vault is a SHARED component (work orders, tool
        // requests and other Files tabs all mount it), so any S3 or auth
        // failure showed every tenant a list of airworthiness paperwork that
        // does not exist. This is the worst variant of the pattern: it fires
        // only on error, which is exactly when a user most needs the truth.
        //
        // Removed 2026-08-23. The list stays empty on failure. Never
        // reintroduce sample documents here.
        try {
          // @ts-ignore
          vault.data.parse([]);
        } catch (e) {
          // ignore parse errors
        }
      });

    vault.events.on("click", async function (id, e) {
      console.debug(id);
      console.debug(e);
      var file = vault.data.getItem(id);
      const result = await Storage.get(file.key, { download: true });
      console.debug(result);
      // { download: true } always resolves to GetResult (never the plain URL
      // string branch) — narrow rather than widen the shared type, so a future
      // caller that omits download: true still gets a compile error instead of
      // an `any`.
      if (typeof result !== "string") {
        downloadBlob(result.Body, file.name);
      }
    });

    
    return () => {
      vaultInstanceRef.current = null;
      vault.destructor();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag-and-drop (upstream 7d866e0d3, ANX-2581): native capture-phase handlers.
  // DHX's own drop can miss when the widget was laid out while hidden or
  // zero-sized; React's capture phase still receives the drop, and we hand the
  // files to the DHX uploader ourselves.
  const allowDrop = (e: DragEvent) => {
    if (props.readOnly) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEnter = (e: DragEvent) => {
    allowDrop(e);
    if (!props.readOnly) setDragging(true);
  };

  const onDragOver = (e: DragEvent) => {
    allowDrop(e);
    if (!props.readOnly) setDragging(true);
  };

  const onDragLeave = (e: DragEvent) => {
    // Only clear when leaving the host, not when crossing child nodes.
    if (e.currentTarget === e.target) {
      setDragging(false);
    }
  };

  const onDrop = (e: DragEvent) => {
    allowDrop(e);
    setDragging(false);
    if (props.readOnly) {
      enqueueSnackbar("View only. You can't upload files.", {
        variant: "error",
      });
      return;
    }
    const vault = vaultInstanceRef.current;
    if (!vault?.uploader || !e.dataTransfer) return;
    try {
      vault.uploader.parseFiles(e.dataTransfer);
    } catch (err) {
      console.error("Vault drop parse failed:", err);
      enqueueSnackbar("Failed to accept dropped files", { variant: "error" });
    }
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: 450 }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            p: 1,
          }}
        >
          <Skeleton
            animation="wave"
            variant="rectangular"
            width="100%"
            height={40}
            sx={{ marginBottom: 0.5 }}
          />
          <Skeleton
            animation="wave"
            variant="rectangular"
            width="100%"
            height={100}
          />
        </Box>
      )}
      <Box
        ref={vaultRef}
        className={`widget-box`}
        onDragEnterCapture={onDragEnter}
        onDragOverCapture={onDragOver}
        onDragLeaveCapture={onDragLeave}
        onDropCapture={onDrop}
        sx={{
          // FILL THE PANEL. The DHX widget root `.dhx_vault` declares only
          // `background-color` + `border` (vault.css) — no width and no height.
          // Its inner `.dhx_vault--container` is width/height:100% OF THAT
          // collapsed box, so the widget took its intrinsic content width and
          // `justifyContent: center` parked it as a narrow vertical sliver in
          // the middle of a wide empty panel (Jay, QA 2026-08-24 — reported on
          // the Company Files tab, but this component is the Files tab of ~30
          // modules, so it was every one of them).
          //
          // `stretch` + an explicit 100% on the widget root is the fix: the
          // container no longer centres an auto-width child, and the root now
          // has a definite box for `.dhx_vault--container` to fill.
          //
          // Keep the host in layout always — never `display: none` while DHX is
          // mounted: that zero-sizes the droppable and breaks drag-and-drop
          // (upstream 7d866e0d3). The loading skeleton overlays it instead.
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          width: '100%',
          height: '100%',
          backgroundColor:"white",
          outline: dragging ? (theme) => `2px dashed ${theme.palette.primary.main}` : 'none',
          outlineOffset: -2,
          '& .dhx_vault': {
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
          },
          '& .dhx_toolbar-button__icon': {
            '&:before': {
              fontSize: 30,
              lineHeight: 30,
            }
          },
          '& .dhx-dropable-area':{
            backgroundColor:"white"
          },
          '& .dhx_vault--toolbar .dhx_toolbar':{
            backgroundColor:"white"
          },
          '& .dhx_widget .dhx_toolbar-button__icon:before ':{
            fontSize:"30px"
          },
          ...(props.readOnly
            ? {
                '& .dhx_dataview-item--remove-btn, & .dhx_item--btn-remove': {
                  display: 'none !important',
                },
              }
            : {})
        }}
      >
      </Box>
    </Box>
  );
}

export default Vault;
